/**
 * Tiny presentational helpers shared across the SPA. Pure functions —
 * no React, no hooks, no DOM access.
 */

/**
 * Render an ISO 4217 amount in minor units (string-shaped to preserve
 * BigInt precision — see PR #126 review) as a human-readable major-
 * unit string, written the way M-Pesa statements write money: currency
 * first, thousands separated.
 *
 *   formatMinorUnits("2500000", "KES") → "KES 25,000.00"
 *   formatMinorUnits("-500", "KES")    → "-KES 5.00"
 *   formatMinorUnits("garbage", "KES") → "KES garbage"
 *
 * Two-decimal currencies are the only shape we serve today (KES, USD,
 * EUR, GBP). When a zero-decimal currency (JPY, KRW) lands on the
 * platform, this helper grows a per-currency dispatch via
 * Intl.NumberFormat — until then the explicit string-arithmetic keeps
 * the contract simple and BigInt-safe.
 */
export function formatMinorUnits(amount: string, currency: string): string {
  if (!/^-?\d+$/.test(amount)) return `${currency} ${amount}`;
  const negative = amount.startsWith("-");
  const digits = negative ? amount.slice(1) : amount;
  const padded = digits.padStart(3, "0");
  const major = padded.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const minor = padded.slice(-2);
  return `${negative ? "-" : ""}${currency} ${major}.${minor}`;
}

const METHOD_TOKENS: Readonly<Record<string, string>> = {
  mpesa: "M-Pesa",
  daraja: "Daraja",
  sasapay: "SasaPay",
  b2b: "B2B",
  b2c: "B2C",
  c2b: "C2B",
  stk: "STK",
};

/**
 * Human label for a wire method name. Unknown words are capitalised, so
 * a new plugin's method still reads acceptably before it gets a token.
 *
 *   formatMethod("mpesa_daraja_b2b") → "M-Pesa Daraja B2B"
 */
export function formatMethod(method: string): string {
  return method
    .split("_")
    .filter(Boolean)
    .map((w) => METHOD_TOKENS[w] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Compact duration: ``47m``, ``3h``, ``2d``. Under a minute is ``<1m``. */
export function formatAge(seconds: number): string {
  if (seconds < 60) return "<1m";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}

/**
 * ``just now`` / ``5 min ago`` / ``3 h ago`` / ``2 d ago``, then the
 * calendar date past 30 days. Returns the input if it won't parse, and
 * ``now`` is injectable so tests stay deterministic.
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 30 * 86_400) return `${Math.floor(seconds / 86_400)} d ago`;
  return new Date(iso).toLocaleDateString();
}
